// THE BRAND SWITCH OF THE HOMEPAGE — a preview tool, not a rebrand (12/10/2026).
// Jérémy is testing a new logo and a new name, "Habit", at /logo-test: the same homepage, the same components, with
// the Habit logo where the North logo is and "Habit" where the page says "North". The real site is North and stays
// North: every component that takes a `brand` defaults to "north", and with that default it is meant to render what
// it rendered before this file existed. What tests/logo-test.test.tsx holds of that is narrower: no Habit name, no
// Habit drawing and no data-brand on the North side, and the North logo and name still there. The equality with the
// page of before, to the character, is not a test: it was measured once, by comparing the served HTML (13/10).
//
// This module is PURE on purpose — no "use client", no React, no DOM: the server components (hero, footer, north-os…)
// and the client ones (nav, skills, daily-claude…) both import it, and what crosses the server → client boundary is
// only ever the string "habit". The drawings that go with it are in brand-logo.tsx, their sizes in habit.css.
//
// The texts that live in data files (lib/faq.ts, sections.ts, meeting-demo/meeting-data.ts, lib/customer-story.ts)
// are NOT rewritten there: they keep saying North, and brandText() below renames them on the way to the page.

export type Brand = "north" | "habit";

/** The legal entity keeps its name whatever the page is called: "© North OS Inc. of New York 2026". */
const LEGAL_NAME = /North OS Inc\./;

/**
 * `text` as the page of `brand` says it. North: the text itself, untouched. Habit: "North" → "Habit" and "NORTH" →
 * "HABIT" wherever the word stands alone ("North OS", "North Deploy", "North's"), except in the company's legal name
 * written with its full stop ("North OS Inc.").
 * Case-sensitive, whole words, and nothing more clever than that: what stays is what is NOT a capitalised or
 * all-caps "North" standing alone — a lowercase "north" (the compass point), northos.com, north.law, #northos,
 * "Northern". A capitalised North that is not the brand is renamed like the brand: "North America" would come out as
 * "Habit America", "North.law" as "Habit.law". No text of the page has one today (13/10); look here before adding one.
 */
export function brandText(text: string, brand: Brand = "north"): string {
  if (brand === "north") return text;
  return text
    .split(new RegExp(`(${LEGAL_NAME.source})`))
    .map((part) => (LEGAL_NAME.test(part) ? part : part.replace(/\bNorth\b/g, "Habit").replace(/\bNORTH\b/g, "HABIT")))
    .join("");
}

/* ---------- the Habit drawing ----------
   The paths of the file Jérémy handed over (newtest.svg, 286 x 70, 12/10), character for character. Two things are
   done to the file, and only these: its clip-path is dropped (a 70 x 70 rectangle around a mark that already fits in
   it: it cuts nothing), and the letters, which the export lists T, I, B, A, H, are put back in reading order — the
   hero raises them one by one, in the order of this array.
   The lockup is ONE block: mark 0–70, a gap of 27, the word 97–286. It is scaled as a whole, from the height of its
   LETTERS (HABIT_LOCKUP and habitMarkHeight below); the word is never sized on its own. */

/** The mark, in a 70 x 70 square: the two arches, then the four bars. */
export const HABIT_MARK = [
  "M48.9557 33.1232H20.9062V0H26.4622V28.0289H43.3998V0H48.9557V33.1232Z",
  "M48.9557 69.9996H43.3998V43.4351H26.4622V69.9996H20.9062V38.3408H48.9557V69.9996Z",
  "M15.7473 11.5293H10.1914V58.7185H15.7473V11.5293Z",
  "M5.55591 23.1719H0V47.9214H5.55591V23.1719Z",
  "M59.677 11.5293H54.1211V58.7185H59.677V11.5293Z",
  "M69.9895 23.1719H64.4336V47.9214H69.9895V23.1719Z",
];

/** HABIT, one path per letter, in reading order. In the lockup's own coordinates: x 97 to 286, y 17 to 53.63. */
export const HABIT_WORD = [
  "M101.573 53.6303H97V17H101.573V32.9284H122.576V17H127.098V53.6303H122.576V36.8477H101.573V53.6303Z",
  "M176.423 53.6303H171.499L168.283 44.9877H151.45L148.234 53.6303H143.461L157.279 17H162.505L176.423 53.6303ZM155.169 34.9383L152.857 41.2192H166.876L164.565 34.9383L160.043 21.6228H159.791L155.169 34.9383Z",
  "M209.734 53.6303H192.801V17H210.136C216.618 17 220.035 20.5173 220.035 26.2957C220.035 30.1648 218.176 33.2801 214.809 34.5363V34.9383C218.929 35.9935 220.99 39.0083 220.99 43.9325C220.99 49.8115 216.769 53.6303 209.734 53.6303ZM197.323 20.869V33.0791H209.835C213.251 33.0791 215.362 31.22 215.362 28.2051V25.6425C215.362 22.6779 213.352 20.869 209.784 20.869H197.323ZM197.323 36.8979V49.7612H209.433C213.503 49.7612 216.166 47.8016 216.166 44.7365V42.0734C216.166 38.8073 213.854 36.8979 210.186 36.8979H197.323Z",
  "M242.295 17V53.6303H237.723V17H242.295Z",
  "M258.664 17H285.999V21.1203H274.643V53.6303H270.12V21.1203H258.664V17Z",
];

/** The lockup's own measures (newtest.svg): the mark's square, the gap, where the word starts and how wide it is, and
 *  the band its letters stand in — flat capitals, every one from y 17 to y 53.6303. */
export const HABIT_LOCKUP = { width: 286, height: 70, mark: 70, gap: 27, wordX: 97, wordWidth: 189, lettersTop: 17, lettersBottom: 53.6303 } as const;

/** How tall the letters are in the file: 36.6303 of the lockup's 70. habit.css writes the same number. */
export const HABIT_LETTERS = 36.6303;

/**
 * THE SIZE RULE (Jérémy, 13/10: « fais la même taille que North »): wherever the whole lockup stands, the letters of HABIT are
 * as tall as the letters of NORTH at that place — 10 px in the hero bar, 7.5 in the capsule… The mark is NOT matched to
 * the North mark: Habit's letters are 52 % of its mark where North's are 31 %, so a mark of equal height made a logo
 * almost twice as loud. `letters` is that height in pixels; this is the height of the mark, and of the lockup, that
 * follows from it.
 */
export function habitMarkHeight(letters: number): number {
  return (letters * HABIT_LOCKUP.height) / HABIT_LETTERS;
}

/** The Habit mark alone as a CSS mask (daily-claude.tsx), where the North one is a file in public/: same drawing as
 *  HABIT_MARK, so the preview adds no asset to the site. */
export const HABIT_MARK_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 70">${HABIT_MARK.map((d) => `<path d="${d}"/>`).join("")}</svg>`,
)}")`;
